import { useMemo } from 'react';

import { EMPTY_ARRAY } from '@e-socna/empty';

import type { ThirdPartiesCmp_SomeThirdParty } from '../queries/some-third-parties.query';

import { useFindAllInternalThirdPartiesQuery } from './useFindAllInternalThirdPartiesQuery.hook';
import { useFindAllExternalThirdPartiesQuery } from './useFindAllExternalThirdPartiesQuery.hook';

interface UnionResult {
  readonly data:
    | Readonly<{
        findSomeThirdParties: readonly ThirdPartiesCmp_SomeThirdParty[];
      }>
    | undefined;
  readonly isLoading: boolean;
  readonly isSuccess: boolean;
}

export const useFindAllThirdPartiesQuery = (enabled = true): UnionResult => {
  const externalQuery = useFindAllExternalThirdPartiesQuery(enabled);
  const internalQuery = useFindAllInternalThirdPartiesQuery(enabled);

  const memoizedResult = useMemo(() => {
    const externalData = externalQuery.data?.findSomeThirdParties;
    const internalData = internalQuery.data?.findSomeThirdParties;

    const data =
      externalData || internalData
        ? {
            findSomeThirdParties: [
              ...(externalData ?? EMPTY_ARRAY),
              ...(internalData ?? EMPTY_ARRAY),
            ],
          }
        : undefined;

    const isLoading = externalQuery.isLoading || internalQuery.isLoading;
    const isSuccess = externalQuery.isSuccess && internalQuery.isSuccess;

    return { data, isLoading, isSuccess };
  }, [
    externalQuery.data?.findSomeThirdParties,
    externalQuery.isLoading,
    externalQuery.isSuccess,
    internalQuery.data?.findSomeThirdParties,
    internalQuery.isLoading,
    internalQuery.isSuccess,
  ]);

  return memoizedResult;
};
